/**
 * Onboarding step asking whether the organization works on its own documents or is an
 * accounting firm managing its clients' documents — the latter declares a master account.
 */
import { useState } from 'react';
import { noop } from 'es-toolkit';
import { Button, Icon, Loader, Modal } from '@insio/ui-toolkit';
import { useDelayedLoading } from '@/hooks/use-delayed-loading';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

interface AccountTypeStepProps {
  /** True while it is not known yet whether the step applies. */
  deciding: boolean;
  /** True from the confirmed firm choice until the declaration fails or the browser leaves. */
  declaring: boolean;
  /** True once a declaration was refused; the firm can then be declared only in the settings. */
  refused: boolean;
  /** Continues onboarding as a company. */
  onCompany: () => void;
  /** Declares the organization a master account. */
  onFirm: () => void;
  /** Leaves onboarding entirely. */
  onSkip: () => void;
}

const CHOICE_CLASS_NAME = 'h-auto w-full whitespace-normal px-md py-lg';
const PRIMARY_CLASS_NAME = 'flex-1 bg-black! hover:bg-foreground-base! text-white!';

export function AccountTypeStep({
  deciding,
  declaring,
  refused,
  onCompany,
  onFirm,
  onSkip
}: AccountTypeStepProps) {
  const t = useFeatureTranslation('onboarding.accountType');
  const showLoader = useDelayedLoading(deciding);
  // The firm card only asks; the declaration is sent from the confirmation.
  const [confirming, setConfirming] = useState(false);
  const showConfirmation = confirming && !refused;

  return (
    <>
      {/* Modal.Header always renders its close button; a declaration in flight cannot be left. */}
      <Modal.Header onClose={declaring ? noop : onSkip}>
        <span className='text-lg font-medium'>{deciding ? null : t('header')}</span>
      </Modal.Header>

      {deciding ? (
        <Modal.Content className='flex items-center justify-center p-xl'>
          {showLoader && <Loader size='sm' />}
        </Modal.Content>
      ) : showConfirmation ? (
        <>
          <Modal.Content className='flex flex-col gap-sm p-lg sm:p-xl'>
            <p className='text-sm font-medium text-foreground-base'>{t('confirm.intro')}</p>
            <ul className='list-disc space-y-2xs pl-lg text-sm text-foreground-muted'>
              <li>{t('confirm.ownBooks')}</li>
              <li>{t('confirm.masterAccountBoard')}</li>
              <li>{t('confirm.subscription')}</li>
            </ul>
          </Modal.Content>

          <Modal.Footer className='flex-col! items-stretch!'>
            <Modal.Action>
              <Button
                variant='default'
                size='md'
                testId='onboarding-account-type-back'
                disabled={declaring}
                onClick={() => setConfirming(false)}
                className='flex-1'
              >
                {t('confirm.back')}
              </Button>
              <Button
                variant='primary'
                size='md'
                testId='onboarding-account-type-confirm'
                disabled={declaring}
                icon={declaring ? <Loader size='sm' /> : undefined}
                onClick={onFirm}
                className={PRIMARY_CLASS_NAME}
              >
                {t('confirm.submit')}
              </Button>
            </Modal.Action>
          </Modal.Footer>
        </>
      ) : (
        <Modal.Content className='flex flex-col gap-lg p-lg sm:p-xl'>
          <p className='text-sm text-foreground-muted'>{t('subtitle')}</p>

          <div className='grid grid-cols-1 gap-md sm:grid-cols-2'>
            <Button
              outline='default'
              orientation='vertical'
              testId='onboarding-account-type-company'
              disabled={declaring}
              onClick={onCompany}
              className={CHOICE_CLASS_NAME}
            >
              <span className='flex flex-col items-center gap-xs'>
                <Icon name='building' size='xl' className='text-foreground-muted' />
                <span className='font-medium text-foreground-base'>{t('company.title')}</span>
                <span className='text-sm text-foreground-muted'>{t('company.description')}</span>
              </span>
            </Button>

            <Button
              outline='default'
              orientation='vertical'
              testId='onboarding-account-type-firm'
              disabled={declaring || refused}
              onClick={() => setConfirming(true)}
              className={CHOICE_CLASS_NAME}
            >
              <span className='flex flex-col items-center gap-xs'>
                <Icon name='briefcase' size='xl' className='text-foreground-muted' />
                <span className='font-medium text-foreground-base'>{t('firm.title')}</span>
                <span className='text-sm text-foreground-muted'>{t('firm.description')}</span>
              </span>
            </Button>
          </div>

          {refused ? (
            <Modal.Info role='status'>{t('refused')}</Modal.Info>
          ) : (
            <Modal.Info>{t('firmHint')}</Modal.Info>
          )}
        </Modal.Content>
      )}
    </>
  );
}
